import { zodResolver } from "@hookform/resolvers/zod";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@marketplace/ui/notifications";
import { addDays } from "date-fns";
import { useAtomValue } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetProductListQuery } from "@/modules/product";

import { createVoucherModalAtom } from "../../atoms";
import { usePostCreateVoucherMutation } from "../mutations/use-post-create-voucher-mutation";
import {
  type CreateVoucherFormSchema,
  useCreateVoucherFormSchema
} from "../schemas/use-create-voucher-form-schema";

export const useCreateVoucherForm = () => {
  const { t } = useTranslation("voucher", {
    keyPrefix: "modal.createVoucherModal"
  });
  const { visible } = useAtomValue(createVoucherModalAtom);
  const resetCreateVoucherModal = useResetAtom(createVoucherModalAtom);
  const schema = useCreateVoucherFormSchema();
  const { mutateAsync, isPending } = usePostCreateVoucherMutation();

  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [debouncedProductSearchQuery] = useDebouncedValue(
    productSearchQuery,
    500
  );

  const { data: productListData, isLoading: productListLoading } =
    useGetProductListQuery({
      queryParams: {
        limit: 100,
        page: 1,
        sort: "newest",
        product_status: "active",
        q: debouncedProductSearchQuery || undefined
      },
      options: {
        enabled: visible
      }
    });

  const formMethods = useForm<CreateVoucherFormSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      voucherCode: "",
      customerEmail: "",
      productId: "",
      productPackageId: "",
      productVariantId: "",
      priceDiscount: 1,
      percentageDiscount: 1,
      validStartDate: new Date(),
      validEndDate: addDays(new Date(), 1),
      status: "active"
    }
  });

  const { handleSubmit, reset } = formMethods;

  const onSubmit: SubmitHandler<CreateVoucherFormSchema> = async (values) => {
    try {
      const res = await mutateAsync({
        voucher_code: values.voucherCode,
        email_customer: values.customerEmail,
        product_id: values.productId,
        product_package_id: +values.productPackageId,
        variant_id: +values.productVariantId,
        price_discount: values.priceDiscount,
        percentage_discount: values.percentageDiscount, // backend will recalculate this
        valid_start_date: values.validStartDate.toISOString(),
        valid_end_date: values.validEndDate.toISOString(),
        status: values.status
      });

      if (res && !res.errors) {
        reset({
          voucherCode: "",
          customerEmail: "",
          productId: "",
          productPackageId: "",
          productVariantId: "",
          priceDiscount: 1,
          percentageDiscount: 1,
          validStartDate: new Date(),
          validEndDate: addDays(new Date(), 1),
          status: "active"
        });
        setProductSearchQuery("");
        notifications.show({
          message: t("notifications.createVoucherSuccess")
        });
        resetCreateVoucherModal();
      }
    } catch {
      notifications.show({
        variant: "error",
        message: t("notifications.createVoucherFailed")
      });
    }
  };

  const resetForm = () => {
    reset({
      voucherCode: "",
      customerEmail: "",
      productId: "",
      productPackageId: "",
      productVariantId: "",
      priceDiscount: 1,
      percentageDiscount: 1,
      validStartDate: new Date(),
      validEndDate: addDays(new Date(), 1),
      status: "active"
    });
    setProductSearchQuery("");
  };

  return {
    formMethods: {
      ...formMethods
    },
    handleSubmit: handleSubmit(onSubmit),
    isPending,
    resetForm,
    productListData,
    productListLoading,
    productSearchQuery,
    setProductSearchQuery
  };
};
